const nextLevelCap = 4;

const splitPath = (raw) => raw.split('/').map(part => part.trim()).filter(Boolean);

const normalizePath = (raw) => splitPath(raw).join(' / ');

const countOf = (entry) => entry.entryCount ?? entry.memberCount ?? 0;
const byPopularity = (a, b) => countOf(b) - countOf(a) || a.path.localeCompare(b.path);
const byPath = (a, b) => a.path.localeCompare(b.path);

const tagSection = (entries, section, { cap = Infinity, sort = byPopularity } = {}) => entries
  .sort(sort)
  .slice(0, cap)
  .map(entry => ({ ...entry, section }));

const startsWith = (parts, prefix) => prefix.every((token, index) => parts[index] === token);

const computeStructuralUnitSuggestions = (list, input) => {
  if ((input ?? '').trim() === '') {
    return { nextLevel: [], complete: tagSection([...list], 'complete', { sort: byPath }) };
  }

  const endsWithSlash = /\/\s*$/.test(input);
  const inputParts = splitPath(input).map(part => part.toLowerCase());
  const listParts = list.map(option => ({
    option,
    originalParts: splitPath(option.path),
    parts: splitPath(option.path).map(part => part.toLowerCase()),
  }));

  const lastTypedIsExistingToken = !endsWithSlash && inputParts.length > 0
    && listParts.some(({ parts }) => parts.length >= inputParts.length && startsWith(parts, inputParts));

  const drillDown = endsWithSlash || lastTypedIsExistingToken;
  const level = drillDown ? inputParts.length + 1 : inputParts.length;
  const lookupPrefix = drillDown ? inputParts : inputParts.slice(0, -1);
  const lastTypedToken = drillDown ? '' : inputParts[inputParts.length - 1] ?? '';

  const nextLevelGroups = new Map();
  listParts.forEach(({ option, originalParts, parts }) => {
    if (parts.length < level) return;
    if (!startsWith(parts, lookupPrefix)) return;
    if (lastTypedToken && !parts[level - 1].startsWith(lastTypedToken)) return;

    const path = originalParts.slice(0, level).join(' / ');
    const key = path.toLowerCase();
    const existing = nextLevelGroups.get(key);
    if (existing) {
      existing.entryCount += countOf(option);
    } else {
      nextLevelGroups.set(key, { path, entryCount: countOf(option) });
    }
  });

  const nextLevel = tagSection(Array.from(nextLevelGroups.values()), 'nextLevel', { cap: nextLevelCap });
  const nextLevelKeys = new Set(nextLevel.map(entry => entry.path.toLowerCase()));
  const needle = normalizePath(input).toLowerCase();

  const completeMatches = list.filter(option => (
    !nextLevelKeys.has(option.path.toLowerCase())
    && normalizePath(option.path).toLowerCase().includes(needle)
  ));

  return { nextLevel, complete: tagSection(completeMatches, 'complete', { sort: byPath }) };
};

export default computeStructuralUnitSuggestions;
